import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type {
  ExchangeShippingRateState,
  ExchangeShippingRatesState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import {
  RedoCurrencyInput,
  RedoCurrencyInputValue,
} from "@redotech/redo-web/arbiter-components/input/redo-currency-input";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export const ExchangeShippingRatesDetails = memo(
  function ExchangeShippingRatesDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<ExchangeShippingRatesState>) {
    const { t } = useTranslation();
    const team = useRequiredContext(TeamContext);
    const shopCurrency = getShopCurrencyWithDefault({ team });
    const canEdit = useHasPermission(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    );

    const nextStepChange = useHandler((next: StepId | undefined) => {
      setState((state) => ({ ...state, next }));
    });

    const updateRate = useHandler(
      (id: string, patch: Partial<ExchangeShippingRateState>) => {
        setState((state) => ({
          ...state,
          rates: state.rates.map((rate) =>
            rate.id === id ? { ...rate, ...patch } : rate,
          ),
        }));
      },
    );

    const removeRate = useHandler((id: string) => {
      setState((state) => ({
        ...state,
        rates: state.rates.filter((rate) => rate.id !== id),
      }));
    });

    const addRate = useHandler(() => {
      setState((state) => ({
        ...state,
        rates: [
          ...state.rates,
          { id: crypto.randomUUID(), name: "", description: "", price: 0 },
        ],
      }));
    });

    return (
      <>
        <Flex dir="column" gap="sm">
          <Flex align="center" gap="sm" justify="space-between">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Exchange shipping rates")}
            </Text>
            {canEdit && (
              <RedoButton
                hierarchy="tertiary"
                IconLeading={PlusIcon}
                onClick={addRate}
                size="xs"
                text={t("Add rate")}
              />
            )}
          </Flex>
          {state.rates.map((rate, index) => {
            const isLast = index === state.rates.length - 1;
            const priceValue: RedoCurrencyInputValue = {
              amount: rate.price,
              currency: shopCurrency,
            };
            return (
              <Flex
                borderBottomWidth={isLast ? undefined : "1px"}
                borderColor="secondary"
                borderStyle="solid"
                dir="column"
                gap="sm"
                key={rate.id}
                pb={isLast ? undefined : "lg"}
              >
                <Flex align="flex-end" gap="sm">
                  <Flex dir="column" flex={1}>
                    <RedoTextInput
                      errorText={
                        rate.name.trim() ? undefined : t("Name is required")
                      }
                      label={t("Name")}
                      placeholder={t("Standard shipping")}
                      setValue={(name) => updateRate(rate.id, { name })}
                      size="sm"
                      state={canEdit ? "default" : "readonly"}
                      value={rate.name}
                    />
                  </Flex>
                  {canEdit && (
                    <RedoButton
                      aria-label={t("Remove rate")}
                      hierarchy="tertiary"
                      IconLeading={TrashIcon}
                      onClick={() => removeRate(rate.id)}
                      size="xs"
                    />
                  )}
                </Flex>
                <RedoTextInput
                  label={t("Description")}
                  placeholder={t("3-5 business days")}
                  setValue={(description) =>
                    updateRate(rate.id, { description })
                  }
                  size="sm"
                  state={canEdit ? "default" : "readonly"}
                  value={rate.description}
                />
                <RedoCurrencyInput
                  errorText={
                    rate.price === undefined || rate.price < 0
                      ? t("Price must be 0 or more")
                      : undefined
                  }
                  label={t("Price")}
                  placeholder={0}
                  setValue={(value) =>
                    updateRate(rate.id, { price: value.amount })
                  }
                  size="sm"
                  state={canEdit ? "default" : "readonly"}
                  value={priceValue}
                />
              </Flex>
            );
          })}
          {!state.rates.length && (
            <Text fontSize="xs" textColor="error">
              {t("Add at least one shipping rate")}
            </Text>
          )}
          <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
            {t(
              "Offered to the customer for the exchange order when the return option charges for new order shipping. Prices are in {{shopCurrency}} and replace the exchange shipping rate tables in settings.",
              { shopCurrency },
            )}
          </Text>
        </Flex>
        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Next step")}
          </Text>
          {stepSelect({ value: state.next, valueChange: nextStepChange })}
        </Flex>
      </>
    );
  },
);
